/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */
import { ofType } from 'redux-observable';
import { of, empty, Observable } from 'rxjs';
import { concatMap, map, mergeMap } from 'rxjs/operators';

import { ActionManagerInstance } from '../../actions/manager';
import Constants, { publicConstants } from '../../constants';
import Utils from '../../utils/utils';
import { getSemanticURL } from '../../router/app-router';
import { getResolvedTargetName } from '../../router/routing-manager';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import * as actionType from '../../store/state/actions';
import * as actions from '../../store/state/actions/action-creators';
import { getDataContextName } from '../../container/container-utils';
import * as StateManager from '../../store/state/state-manager';
import {
  addPreviewResetAction,
  getActionBasedOnContainerMode,
  getActionPayload,
  getActivationDetails,
  getContext,
  getDirtyDetails,
  isAddContainerAction,
  processDataToDispatchErrorAction,
  processViewResponse
} from '../../store/state/epics/util';
import type { Action } from '../../store/state/state-manager';
import { getCurrentCaseViewMode } from '../../case/common-helper';

import executeAsyncRequest from './async-request';

const { RESOURCE_TYPES } = publicConstants;

const executeShowDataAsyncRequest = (payload: Action['payload'], actionMgrID: number) => {
  const {
    context,
    pageName,
    dataContext,
    dataContextParameters,
    skipSemanticUrl = false,
    isDeferLoaded = false,
    parentAction = '',
    resourceType,
    containerName,
    RemoteSystemID
  } = payload;
  const target = getResolvedTargetName(containerName, context);
  let semanticURL = '';
  let removeActionPayload = {};
  let continueExecution = false;

  if (!skipSemanticUrl) {
    semanticURL = getSemanticURL(
      actionType.ACTION_SHOWDATA,
      {
        pageName,
        dataViewName: dataContext
      },
      dataContextParameters
    );
    const { activate, activateContainerActionPayload } = getActivationDetails(RESOURCE_TYPES.PAGE, target, {
      semanticURL
    });

    if (activate) {
      return of({
        type: actionType.ACTIVATE_CONTAINER_ITEM,
        activateContainerActionPayload,
        actionConfig: payload
      });
    }

    ({ continueExecution, removeActionPayload } = getDirtyDetails(target));

    if (!continueExecution) {
      ActionManagerInstance.rejectActionByID(actionMgrID);
      return empty();
    }
  }

  return executeAsyncRequest(
    {
      context,
      pageName,
      dataContext,
      dataContextParameters,
      semanticURL,
      skipSemanticUrl,
      containerName,
      isDeferLoaded,
      parentAction,
      resourceType,
      RemoteSystemID
    },
    { removeActionPayload }
  );
};

const showData = (action$: Observable<Action>) => {
  // Need to define these in this scope so they can be set/used
  //  in various parts of the processing.
  let actionMgrID = 0;
  let theReturn: any[] = [];
  let rootView: any;
  return action$.pipe(
    ofType(actionType.SHOW_DATA),
    concatMap((action: Action) => {
      // extract the Action Manager ID from the payload
      ({ actionMgrID } = action.payload);
      return of(action.payload).pipe(
        mergeMap((payload) => executeShowDataAsyncRequest(payload, actionMgrID)),
        mergeMap(async (data: any) => {
          if (data && data.type === actionType.ERROR) {
            // processDataToDispatchErrorAction calls ActionManagerInstance.rejectActionByID
            theReturn.push(processDataToDispatchErrorAction(data));
            return theReturn;
          }

          const { type, actionConfig, headers, response, skipSemanticUrl } = data;
          rootView = Utils.getIn(response, Constants.ROOT);
          const { containerName, parentAction, resourceType } = actionConfig;
          const target = getResolvedTargetName(containerName, actionConfig.context);
          let translatedDXAPIResponse;

          const currentCaseViewMode = getCurrentCaseViewMode(actionConfig?.context);

          // Defer loaded tab pages should merged the data received
          if (actionConfig.isDeferLoaded) {
            translatedDXAPIResponse = getTranslatedDXAPIResponse(response);
            translatedDXAPIResponse.directUrlUpdation = !skipSemanticUrl;
            const options = { acName: target, context: actionConfig.context };
            await processViewResponse(translatedDXAPIResponse, actionConfig, options);
            const commonInfo = {
              target,
              headers,
              context: actionConfig.context,
              containerItemID: data.containerItemID
            };
            const payload = getActionPayload(translatedDXAPIResponse, commonInfo);
            theReturn = actions.postProcess(actions.mergeData, payload);
            return theReturn;
          }

          if (type === actionType.ACTIVATE_CONTAINER_ITEM) {
            const {
              activateContainerActionPayload: { semanticURL, containerItemID }
            } = data;
            const context = getDataContextName(containerItemID);
            const options = { acName: target, context };
            await processViewResponse({ directUrlUpdation: true, semanticURL }, actionConfig, options, true);
            theReturn.push(
              actions.postProcess(actions.activateContainerItem, {
                target,
                containerItemID,
                resourceType: RESOURCE_TYPES.DATA
              })
            );
          } else if (resourceType === 'DATA' && parentAction === actionType.SUBMIT_DATA_OBJECT_ACTION) {
            // We need to trigger MERGE_DATA when we have an existing Preview and
            // source of this action is from SUBMIT_DATA_OBJECT_ACTION
            const { context } = actionConfig;
            const responseData = response?.data;
            const mergeDataPayload = {
              context,
              data: responseData
            };
            theReturn.push(actions.postProcess(actions.mergeData, mergeDataPayload));
          } else {
            const context = getContext(target, containerName, actionConfig.context);
            const { removeActionPayload } = data;
            translatedDXAPIResponse = getTranslatedDXAPIResponse(response);
            translatedDXAPIResponse.directUrlUpdation = !skipSemanticUrl;
            const options = { acName: target, context };
            await processViewResponse(translatedDXAPIResponse, actionConfig, options);
            const commonInfo = { target, context, containerItemID: context, headers, currentCaseViewMode };
            const payload = getActionPayload(translatedDXAPIResponse, commonInfo);
            payload.resourceType = RESOURCE_TYPES.DATA;
            const actionBasedOnContainerMode = getActionBasedOnContainerMode(target, payload);

            if (removeActionPayload.containerItemID && isAddContainerAction(actionBasedOnContainerMode)) {
              theReturn.push(
                actions.postProcess(actions.removeContainerItem, {
                  containerItemID: removeActionPayload.containerItemID,
                  target
                })
              );
            }
            /**
             * addPreviewResetAction method will trigger RESET_CONTAINERS which removes the
             * Preview Containers. We need to do this before ADD_CONTAINER_ITEM else it will impact
             * Preview usecase for data objects.
             */
            addPreviewResetAction(theReturn);
            theReturn.push(actionBasedOnContainerMode);
          }
          return theReturn;
        }),
        map(() => {
          // this is the right place to note that the promise should resolve...
          ActionManagerInstance.resolveActionByID(actionMgrID, rootView);
          StateManager.dispatch(theReturn);
          theReturn = [];
          return theReturn;
        })
      );
    })
  );
};

export default showData;
